// What each record import fills in. `headers` are the column names that pick a
// field automatically, compared after normalizeHeader(); earlier entries win
// when a file has more than one candidate. Names come from Ivy's account export,
// Houzz Pro's contact list and plain spreadsheets.
//
// Field kinds decide how a cell is read: text (the default), multiline, email,
// phone, state, zip, bool, number, hours and date.

const STREET = ['address', 'street', 'street address', 'address 1', 'address line 1'];
const STREET2 = ['address2', 'address 2', 'address line 2', 'suite', 'apt', 'unit'];
const CITY = ['city', 'town'];
const STATE = ['state', 'province', 'state province', 'region'];
const ZIP = ['zip', 'zip code', 'zipcode', 'postal code', 'postcode'];
const ARCHIVED = ['is archived', 'archived', 'inactive'];

export const RECORD_TYPES = {
  customers: {
    label: 'Clients',
    noun: 'client',
    nouns: 'clients',
    sheetNames: [
      'clients',
      'customers',
      'client list',
      'customer list',
      'contacts',
      'contact list',
    ],
    fields: [
      {
        key: 'name',
        label: 'Client Name',
        headers: [
          'name',
          'client name',
          'customer name',
          'display name',
          'full name',
          'client',
          'customer',
          'contact name',
        ],
      },
      { key: 'firstName', label: 'First Name', headers: ['first name', 'firstname', 'first'] },
      {
        key: 'lastName',
        label: 'Last Name',
        headers: ['last name', 'lastname', 'last', 'surname'],
      },
      // Houzz Pro lists each person on a row of their own under their
      // household's Account Name ("Ellis Residence").
      {
        key: 'accountName',
        label: 'Household / Account (its rows become one client)',
        headers: ['account name', 'household', 'household name'],
      },
      {
        key: 'role',
        label: 'Label / Role (vendor rows are left out)',
        headers: ['label role', 'role', 'contact type'],
      },
      {
        key: 'company',
        label: 'Company',
        headers: ['company name', 'company', 'business name', 'organization'],
      },
      {
        key: 'email',
        label: 'Email',
        kind: 'email',
        headers: ['email', 'email address', 'e mail', 'primary email', 'client email'],
      },
      {
        key: 'secondaryEmail',
        label: 'Second Email',
        kind: 'email',
        headers: ['second email', 'secondary email', 'email 2', 'alternate email', 'other email'],
      },
      {
        key: 'phone',
        label: 'Phone',
        kind: 'phone',
        headers: [
          'phone',
          'phone number',
          'mobile',
          'mobile phone',
          'cell',
          'cell phone',
          'primary phone',
          'telephone',
        ],
      },
      {
        key: 'secondaryPhone',
        label: 'Second Phone',
        kind: 'phone',
        headers: ['second phone', 'secondary phone', 'phone 2', 'alternate phone', 'work phone'],
      },
      {
        key: 'street',
        label: 'Billing Street',
        headers: [...STREET, 'billing address', 'mailing address'],
      },
      { key: 'street2', label: 'Billing Address Line 2', headers: STREET2 },
      { key: 'city', label: 'Billing City', headers: CITY },
      { key: 'state', label: 'Billing State', kind: 'state', headers: STATE },
      { key: 'zip', label: 'Billing Zip', kind: 'zip', headers: ZIP },
      {
        key: 'notes',
        label: 'Notes',
        kind: 'multiline',
        headers: ['notes', 'note', 'comments', 'additional info'],
      },
      { key: 'tags', label: 'Tags (added to notes)', headers: ['tags', 'tag'] },
      { key: 'archived', label: 'Archived', kind: 'bool', headers: ARCHIVED },
    ],
  },

  vendors: {
    label: 'Vendors',
    noun: 'vendor',
    nouns: 'vendors',
    sheetNames: ['vendors', 'vendor list', 'suppliers'],
    fields: [
      {
        key: 'name',
        label: 'Vendor Name',
        headers: [
          'company name',
          'vendor name',
          'vendor',
          'name',
          'company',
          'business name',
          'supplier',
        ],
      },
      {
        key: 'firstName',
        label: 'Contact First Name',
        headers: ['first name', 'contact first name'],
      },
      { key: 'lastName', label: 'Contact Last Name', headers: ['last name', 'contact last name'] },
      {
        key: 'contactName',
        label: 'Contact Name',
        headers: ['contact name', 'contact', 'contact person', 'sales rep', 'rep'],
      },
      // A contact list holding clients and vendors (Houzz Pro's) says which is
      // which here.
      {
        key: 'role',
        label: 'Label / Role (only vendor rows are imported)',
        headers: ['label role', 'role', 'contact type'],
      },
      {
        key: 'email',
        label: 'Email',
        kind: 'email',
        headers: ['email', 'email address', 'e mail', 'ordering email'],
      },
      {
        key: 'phone',
        label: 'Phone',
        kind: 'phone',
        headers: ['phone', 'phone number', 'telephone', 'office phone'],
      },
      {
        key: 'accountNumber',
        label: 'Account Number',
        headers: ['account number', 'account #', 'account no', 'account'],
      },
      { key: 'link', label: 'Website', headers: ['website', 'web site', 'url', 'link'] },
      { key: 'street', label: 'Street', headers: STREET },
      { key: 'street2', label: 'Address Line 2', headers: STREET2 },
      { key: 'city', label: 'City', headers: CITY },
      { key: 'state', label: 'State', kind: 'state', headers: STATE },
      { key: 'zip', label: 'Zip', kind: 'zip', headers: ZIP },
      {
        key: 'description',
        label: 'What They Sell (added to notes)',
        headers: ['description', 'type', 'category', 'specialty'],
      },
      {
        key: 'notes',
        label: 'Internal Notes',
        kind: 'multiline',
        headers: ['notes', 'note', 'internal notes', 'additional info'],
      },
      { key: 'archived', label: 'Archived', kind: 'bool', headers: ARCHIVED },
    ],
  },

  projects: {
    label: 'Projects',
    noun: 'project',
    nouns: 'projects',
    sheetNames: ['projects', 'project list', 'jobs'],
    fields: [
      {
        key: 'name',
        label: 'Project Name',
        headers: [
          'project name',
          'project',
          'name',
          'job name',
          'job',
          'project title',
          'nickname',
        ],
      },
      {
        key: 'clientName',
        label: 'Client Name',
        headers: ['client name', 'client', 'customer name', 'customer'],
      },
      {
        key: 'email',
        label: 'Client Email',
        kind: 'email',
        headers: ['email', 'client email', 'email address', 'customer email'],
      },
      {
        key: 'secondaryEmail',
        label: 'Second Email',
        kind: 'email',
        headers: ['second email', 'secondary email'],
      },
      {
        key: 'phone',
        label: 'Client Phone',
        kind: 'phone',
        headers: ['phone number', 'phone', 'client phone'],
      },
      {
        key: 'street',
        label: 'Project Street',
        headers: [...STREET, 'project address', 'site address'],
      },
      { key: 'street2', label: 'Project Address Line 2', headers: STREET2 },
      { key: 'city', label: 'Project City', headers: CITY },
      { key: 'state', label: 'Project State', kind: 'state', headers: STATE },
      { key: 'zip', label: 'Project Zip', kind: 'zip', headers: ZIP },
      {
        key: 'taxName',
        label: 'Sales Tax',
        headers: ['tax name', 'sales tax', 'tax location', 'tax rate name'],
      },
      {
        key: 'notes',
        label: 'Notes',
        kind: 'multiline',
        headers: ['notes', 'note', 'description'],
      },
      {
        key: 'notes2',
        label: 'More Notes (added to notes)',
        kind: 'multiline',
        headers: ['general notes', 'additional notes', 'other notes'],
      },
      {
        key: 'createdAt',
        label: 'Started',
        kind: 'date',
        headers: ['created at', 'created', 'date created', 'created date', 'start date'],
      },
      {
        key: 'archived',
        label: 'Archived (imported as inactive)',
        kind: 'bool',
        headers: [...ARCHIVED, 'closed', 'status'],
      },
    ],
  },

  taxLocations: {
    label: 'Sales Tax Rates',
    noun: 'tax rate',
    nouns: 'tax rates',
    sheetNames: ['sales taxes', 'sales tax', 'tax rates', 'taxes'],
    fields: [
      {
        key: 'name',
        label: 'Tax Name (City, State)',
        headers: ['tax name', 'name', 'tax location', 'location', 'jurisdiction'],
      },
      { key: 'city', label: 'City', headers: CITY },
      { key: 'state', label: 'State', kind: 'state', headers: STATE },
      {
        key: 'rate',
        label: 'Rate %',
        kind: 'number',
        headers: ['rate', 'tax rate', 'rate %', 'sales tax rate', 'percentage', 'tax'],
      },
      { key: 'agency', label: 'Agency', headers: ['agency', 'tax agency', 'authority'] },
      { key: 'archived', label: 'Archived', kind: 'bool', headers: ARCHIVED },
    ],
  },

  rooms: {
    label: 'Project Rooms',
    noun: 'room',
    nouns: 'rooms',
    sheetNames: ['rooms', 'room list', 'areas'],
    fields: [
      { key: 'projectName', label: 'Project Name', headers: ['project name', 'project', 'job'] },
      { key: 'name', label: 'Room Name', headers: ['name', 'room name', 'room', 'area', 'space'] },
    ],
  },

  // A sheet with one row per proposal line: a Houzz Pro FF&E or Global Tracker
  // export, Ivy's project tracker, a Design Manager, Programa or Studio
  // Designer item list, or any spreadsheet. Rows are grouped into proposals by
  // project and proposal number. Ivy's account export needs no mapping; its
  // Invoices and Invoice Items sections are read by proposalImport.js.
  proposals: {
    label: 'Proposals',
    noun: 'proposal',
    nouns: 'proposals',
    sheetNames: [
      'invoices',
      'invoice items',
      'proposals',
      'proposal items',
      'proposal lines',
      'estimates',
      'estimate items',
      'ffe schedule',
      'ff e schedule',
      'specifications',
      'global tracker',
      'global tracker report',
    ],
    fields: [
      {
        key: 'projectName',
        label: 'Project Name',
        headers: ['project name', 'project', 'job name', 'job', 'client project'],
      },
      {
        key: 'proposal',
        label: 'Proposal Number or Name',
        headers: [
          'proposal',
          'proposal number',
          'proposal #',
          'proposal no',
          'estimate',
          'estimate number',
          'estimate #',
          'document number',
          'document progress',
        ],
      },
      {
        key: 'date',
        label: 'Proposal Date',
        kind: 'date',
        headers: ['proposal date', 'estimate date', 'document date', 'date'],
      },
      {
        key: 'room',
        label: 'Room',
        headers: ['room', 'room name', 'location', 'area', 'space', 'sidemark', 'side mark'],
      },
      {
        key: 'name',
        label: 'Item Name',
        headers: [
          'title original',
          'title',
          'item name',
          'item',
          'name',
          'product name',
          'product',
        ],
      },
      {
        key: 'clientName',
        label: 'Client-Facing Name',
        headers: ['title client facing', 'client facing title', 'client title'],
      },
      {
        key: 'description',
        label: 'Description',
        kind: 'multiline',
        headers: [
          'client description',
          'client facing description',
          'description',
          'item description',
        ],
      },
      {
        key: 'vendor',
        label: 'Vendor',
        headers: ['vendor', 'vendor name', 'vendor or sub', 'supplier', 'manufacturer'],
      },
      {
        key: 'sku',
        label: 'SKU',
        headers: ['sku', 'model number', 'model #', 'item number', 'item #', 'code'],
      },
      {
        key: 'qty',
        label: 'Quantity',
        kind: 'number',
        headers: ['selling quantity', 'quantity', 'qty'],
      },
      {
        key: 'unitCost',
        label: 'Unit Cost (before markup)',
        kind: 'number',
        headers: [
          'unit purchase cost',
          'purchase unit cost',
          'unit cost',
          'cost',
          'trade price',
          'net price',
          'rate',
        ],
      },
      {
        key: 'markup',
        label: 'Markup %',
        kind: 'number',
        headers: ['markup %', 'markup', 'markup percent', 'markup percentage', 'mark up'],
      },
      {
        key: 'unitPrice',
        label: 'Unit Selling Price',
        kind: 'number',
        headers: [
          'unit selling price',
          'unit price',
          'selling price',
          'sell price',
          'sell',
          'client price',
          'price',
        ],
      },
      {
        key: 'totalPrice',
        label: 'Total Selling Price',
        kind: 'number',
        headers: [
          'total selling price',
          'selling total',
          'ext sell',
          'extended sell',
          'ext price',
          'extended price',
          'line total',
          'total price',
          'total',
        ],
      },
      {
        key: 'shipping',
        label: 'Shipping Cost',
        kind: 'number',
        headers: ['shipping purchase cost', 'shipping cost', 'shipping', 'freight', 'freight cost'],
      },
      {
        key: 'shippingMarkup',
        label: 'Shipping Markup %',
        kind: 'number',
        headers: ['shipping markup', 'shipping markup %'],
      },
      {
        key: 'msrp',
        label: 'MSRP',
        kind: 'number',
        headers: ['msrp', 'retail price', 'list price'],
      },
      {
        key: 'imageUrl',
        label: 'Image Link',
        headers: ['image link', 'image url', 'image', 'photo'],
      },
      {
        key: 'link',
        label: 'Product Link',
        headers: ['website link', 'product link', 'product url', 'url', 'link'],
      },
      {
        key: 'dimensions',
        label: 'Dimensions (custom spec)',
        headers: ['dimensions', 'dimension', 'size'],
      },
      {
        key: 'finishes',
        label: 'Finish / Color',
        headers: ['finish color', 'finish', 'finishes', 'color'],
      },
      {
        key: 'materials',
        label: 'Materials',
        headers: ['materials fabric', 'materials', 'material'],
      },
      { key: 'unitType', label: 'Unit', headers: ['unit type', 'unit', 'uom'] },
      {
        key: 'approval',
        label: 'Client Approval (declined lines are left out)',
        headers: ['client approval status', 'approval status', 'client approval', 'approval'],
      },
      { key: 'taxable', label: 'Taxable', kind: 'bool', headers: ['taxable item', 'taxable'] },
      {
        key: 'shippingTaxable',
        label: 'Shipping Taxable',
        kind: 'bool',
        headers: ['taxable shipping', 'shipping taxable'],
      },
      { key: 'category', label: 'Category', headers: ['category', 'category name', 'section'] },
    ],
  },

  timeEntries: {
    label: 'Time Entries',
    noun: 'time entry',
    nouns: 'time entries',
    sheetNames: ['time tracks', 'time entries', 'time', 'timesheet', 'timesheets', 'time tracking'],
    fields: [
      { key: 'projectName', label: 'Project Name', headers: ['project name', 'project', 'job'] },
      {
        key: 'date',
        label: 'Date',
        kind: 'date',
        headers: ['date', 'work date', 'entry date', 'day'],
      },
      {
        key: 'hours',
        label: 'Hours',
        kind: 'hours',
        headers: ['duration', 'hours', 'billable hours', 'time', 'hrs', 'quantity'],
      },
      {
        key: 'rate',
        label: 'Rate (per hour)',
        kind: 'number',
        headers: ['rate', 'hourly rate', 'bill rate', 'billing rate'],
      },
      {
        key: 'description',
        label: 'Description',
        kind: 'multiline',
        headers: ['description', 'notes', 'note', 'details', 'task'],
      },
      {
        key: 'employee',
        label: 'Employee',
        headers: ['name', 'employee', 'team member', 'staff', 'user', 'person'],
      },
      { key: 'activity', label: 'Activity', headers: ['activity', 'service', 'task type', 'type'] },
      { key: 'billable', label: 'Billable', kind: 'bool', headers: ['billable'] },
      { key: 'taxable', label: 'Taxable', kind: 'bool', headers: ['taxable'] },
      {
        key: 'employeeRate',
        label: 'Employee Cost (per hour)',
        kind: 'number',
        headers: ['cost', 'cost rate', 'employee rate', 'pay rate'],
      },
    ],
  },
};

// The Destination menu, in the order it's listed. 'catalog' is the product and
// service import; the rest are RECORD_TYPES.
export const DESTINATIONS = [
  { key: 'catalog', label: 'Products & Services' },
  { key: 'customers', label: 'Clients' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'projects', label: 'Projects' },
  { key: 'proposals', label: 'Proposals' },
  { key: 'rooms', label: 'Project Rooms' },
  { key: 'timeEntries', label: 'Time Entries' },
  { key: 'taxLocations', label: 'Sales Tax Rates' },
];

// Sheet or section names that hold products or services.
export const CATALOG_SHEET_NAMES = {
  product: ['products', 'product library', 'catalog', 'items', 'project tracker'],
  service: ['services', 'service list'],
};

// The order a whole-account export goes in, so each step can link to the one
// before it: projects to clients and tax rates, rooms, proposals and time to
// projects, and products to vendors.
export const IMPORT_ORDER = [
  { destination: 'taxLocations' },
  { destination: 'customers' },
  { destination: 'vendors' },
  { destination: 'projects' },
  { destination: 'rooms' },
  { destination: 'proposals' },
  { destination: 'timeEntries' },
  { destination: 'catalog', itemType: 'product' },
  { destination: 'catalog', itemType: 'service' },
];
